import React, { useState } from 'react'
import { Clock } from 'lucide-react'
import { RoomModel } from '../../common/models/RoomModel.js'
import { BookingModal } from './BookingModal.js'
import {
    generateTimeSlots,
    getEventsBetween,
    isTimeSelected,
} from '../utils/calendar-utils.js'
import { RoomStatus } from '../../common/types.js'

export interface BaseCalendarProps {
    rooms: RoomModel[]
    singleRoom?: boolean
    className?: string
    title?: string
    maxHeight?: string
}

export function BaseCalendar(props: BaseCalendarProps) {
    const { rooms, singleRoom = false, className = '', title = 'Today\'s Schedule', maxHeight = '50vh' } = props
    const [selectedRoom, setSelectedRoom] = useState<string | null>(null)
    const [selectedTime, setSelectedTime] = useState<Date | null>(null)
    const [showModal, setShowModal] = useState(false)

    const handleCloseModal = () => {
        setShowModal(false)
        setSelectedRoom(null)
        setSelectedTime(null)
    }

    const timeSlots = generateTimeSlots()

    // Check if a room is selected
    const isRoomSelected = (roomId: string): boolean => {
        return selectedRoom === roomId
    }

    const renderBookingModal = () => {
        if (!selectedRoom && !singleRoom) {
            return null
        }

        const roomId = singleRoom ? rooms[0].id : selectedRoom
        const roomName = singleRoom ? rooms[0].name : rooms.find(r => r.id === selectedRoom)?.name

        if (!roomId || !roomName || !selectedTime) {
            return null
        }

        return (
            <BookingModal
                isOpen={showModal}
                onClose={handleCloseModal}
                roomId={roomId}
                roomName={roomName}
                selectedTime={selectedTime}
            />
        )
    }

    const getContent = (room: RoomModel, events: any[], isAvailable: boolean) => {
        if (!room.isBookable) {
            return (
                <div className="text-gray-600 flex items-center">
                    <span>Not bookable</span>
                </div>
            )
        } else if (isAvailable) {
            return (
                <div className="text-gray-400 flex items-center">
                    <Clock className="w-4 h-4 mr-1" />
                    <span>Book</span>
                </div>
            )
        }

        return events.map(event => (
            <div className="mb-1 last:mb-0" key={event.id}>
                <div className="text-sm font-medium">{event.userName}</div>
                <div className="text-xs text-gray-500">
                    {new Date(event.startTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} -
                    {new Date(event.endTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </div>
            </div>
        ))
    }

    const getColor = (room: RoomModel, isAvailable: boolean, isSelected: boolean) => {
        if (!room.isBookable) {
            return 'bg-gray-200'
        } else if (isAvailable) {
            return 'bg-[#f8fff8] cursor-pointer hover:bg-gray-100'
        } else if (isSelected) {
            return 'bg-gray-200'
        }

        return 'bg-rose-50'
    }

    return (
        <div className={`bg-white rounded-[20px] p-4 ${className}`}>
            {renderBookingModal()}

            <h2 className="text-lg font-bold mb-4">{title}</h2>

            <div className="overflow-y-auto overflow-x-auto" style={{ maxHeight }}>
                <table className={singleRoom ? 'w-full border-collapse' : 'min-w-full border-collapse'}>
                    <thead className="sticky top-0 z-20">
                    <tr>
                        <th className="py-2 px-3 text-left text-sm font-medium text-gray-500 border-b left-0 bg-white z-10">Time</th>
                        {singleRoom ? (
                            <th className="py-2 px-3 text-left text-sm font-medium text-gray-500 border-b bg-white">Status</th>
                        ) : (
                            rooms.map((room) => (
                                <th
                                    key={room.id}
                                    className="py-2 px-3 text-center text-sm font-medium border-b bg-white"
                                >
                                    <div className="flex flex-col items-center">
                                        <span>{room.name}</span>
                                        {room.status === RoomStatus.AVAILABLE ? (
                                            <div className="px-2 py-1 mt-1 bg-[#d0fae5] border border-[#a4f4cf] text-[#006045] text-xs rounded w-full text-center">
                                                Available
                                            </div>
                                        ) : (
                                            <div className="px-2 py-1 mt-1 bg-rose-100 border border-[#ffccd3] text-[#a50036] text-xs rounded w-full text-center">
                                                In Use
                                            </div>
                                        )}
                                    </div>
                                </th>
                            ))
                        )}
                    </tr>
                    </thead>
                    <tbody>
                    {timeSlots.map((timeSlot) => {
                        return (
                            <tr key={timeSlot.startTime.toISOString()}>
                                <td className={`py-2 px-3 text-sm font-medium border-b ${singleRoom ? 'text-center w-[80px]' : ''}`}>
                                    {timeSlot.displayTime}
                                </td>
                                {singleRoom ? (
                                    // Single room view
                                    (() => {
                                        const room = rooms[0]
                                        const events = getEventsBetween(room, timeSlot.startTime, timeSlot.endTime)
                                        const isAvailable = events.length === 0 && room.isBookable
                                        const isSelected = isTimeSelected(selectedTime, timeSlot.startTime)

                                        return (
                                            <td
                                                className={`py-2 px-3 text-sm border-b ${getColor(room, isAvailable, isSelected)}`}
                                                onClick={() => {
                                                    if (isAvailable) {
                                                        setSelectedTime(timeSlot.startTime)
                                                        setSelectedRoom(room.id)
                                                        setShowModal(true)
                                                    }
                                                }}
                                            >
                                                {getContent(room, events, isAvailable)}
                                            </td>
                                        )
                                    })()
                                ) : (
                                    // Multiple rooms view
                                    rooms.map((room) => {
                                        const events = getEventsBetween(room, timeSlot.startTime, timeSlot.endTime)
                                        const isAvailable = events.length === 0 && room.isBookable
                                        const isSelected = isRoomSelected(room.id) && isTimeSelected(selectedTime, timeSlot.startTime)
                                        const classNames = [
                                            'py-2 px-3 text-center text-sm border border-r-gray-200 border-l-gray-200 transition-colors',
                                            getColor(room, isAvailable, isSelected),
                                        ]

                                        return (
                                            <td
                                                key={`${room.id}-${timeSlot.startTime.toISOString()}`}
                                                className={classNames.join(' ')}
                                                onClick={(e) => {
                                                    e.stopPropagation()
                                                    if (isAvailable) {
                                                        setSelectedTime(timeSlot.startTime)
                                                        setSelectedRoom(room.id)
                                                        setShowModal(true)
                                                    }
                                                }}
                                            >
                                                {getContent(room, events, isAvailable)}
                                            </td>
                                        )
                                    })
                                )}
                            </tr>
                        )
                    })}
                    </tbody>
                </table>
            </div>
        </div>
    )
}
